// Rank Forge / InPhase — Markdown export builders.
//
// Every builder returns a self-contained .md file that an AI coding/SEO
// agent (or a human) can act on WITHOUT any other context. Each report
// starts with a "How to use this file" primer, followed by a data section,
// followed by a prioritized "Action agenda" listing concrete todos.

const fmt = (n, d = 0) => (n === null || n === undefined || Number.isNaN(n) ? "—" : Number(n).toLocaleString(undefined, { maximumFractionDigits: d }));

const _cell = (c) => (c === null || c === undefined ? "—" : String(c).replace(/\|/g, "\\|").replace(/\n/g, " "));
const table = (headers, rows) => {
    const H = `| ${headers.join(" | ")} |`;
    const S = `| ${headers.map(() => "---").join(" | ")} |`;
    const R = rows.map((r) => `| ${r.map(_cell).join(" | ")} |`);
    return [H, S, ...R].join("\n");
};

const header = ({ title, project, subtitle, agentPrimer, generatedAt = new Date() }) => {
    const lines = [
        `# ${title}`,
        subtitle ? `_${subtitle}_` : "",
        "",
        `- **Project:** ${project?.name || "—"}${project?.domain ? ` · \`${project.domain}\`` : ""}`,
        `- **Generated:** ${generatedAt.toISOString()}`,
        `- **Source:** InPhase (https://inphase.io)`,
        "",
        "## How to use this file",
        agentPrimer || "This document is a self-contained SEO artifact. Every issue includes its severity and a concrete fix. Work through the **Action agenda** at the bottom in order.",
        "",
        "---",
        "",
    ];
    return lines.filter((l) => l !== null && l !== undefined).join("\n");
};

const actionAgenda = (items) => {
    if (!items || !items.length) return "";
    return [
        "",
        "---",
        "",
        "## Action agenda (do these in order)",
        "",
        ...items.map((a, i) => {
            const prefix = a.priority ? `**[${a.priority.toUpperCase()}]** ` : "";
            const detail = a.detail ? `\n   → ${a.detail}` : "";
            return `${i + 1}. ${prefix}${a.action}${detail}`;
        }),
        "",
    ].join("\n");
};

// ==================== DASHBOARD ====================
export function buildDashboardMd({ project, data, digest }) {
    const md = [];
    const kpis = data?.kpis || {};
    md.push(header({
        title: "Dashboard — Rank velocity snapshot",
        subtitle: "KPIs, alerts, top-movers, and the AI weekly digest",
        project,
        agentPrimer:
            "Use this file to brief the team (or an AI assistant) on the project's current search health. **Alerts** section lists live issues needing attention. **Top-movers** highlight the biggest week-over-week rank shifts. **Action agenda** at the bottom prioritizes what to do next.",
    }));

    md.push("## Current KPIs");
    md.push(table(
        ["Metric", "Value", "Delta"],
        [
            ["Visibility index", `${fmt(kpis.visibility?.value ?? kpis.visibility, 1)} %`, kpis.visibility?.delta ?? "—"],
            ["Est. organic traffic", fmt(kpis.traffic?.value ?? kpis.traffic), kpis.traffic?.delta_pct ? `${kpis.traffic.delta_pct}%` : "—"],
            ["Tracked keywords", fmt(kpis.keywords?.count ?? kpis.keywords), ""],
            ["AI Overview coverage", fmt(kpis.aio?.count ?? kpis.aio), ""],
            ["Average position", fmt(kpis.avg_position?.value ?? kpis.avg_position, 1), kpis.avg_position?.delta ?? "—"],
        ],
    ));

    if (Array.isArray(data?.alerts) && data.alerts.length) {
        md.push("");
        md.push("## Live alerts");
        data.alerts.forEach((a) => {
            md.push(`### ${(a.severity || "info").toUpperCase()} · ${a.title || a.id}`);
            if (a.body) md.push(a.body);
            if (a.action_url) md.push(`\n**Action URL:** \`${a.action_url}\``);
            md.push("");
        });
    }

    if (Array.isArray(data?.top_movers) && data.top_movers.length) {
        md.push("## Top movers this week");
        md.push(table(
            ["Keyword", "Position", "Previous", "Change", "Volume", "Difficulty"],
            data.top_movers.map((k) => [
                k.keyword,
                k.position ?? "—",
                k.previous_position ?? "—",
                k.change ?? "—",
                fmt(k.volume),
                k.difficulty ?? "—",
            ]),
        ));
    }

    if (digest?.summary) {
        md.push("");
        md.push("## AI weekly digest");
        md.push(digest.summary);
        if (Array.isArray(digest.action_items) && digest.action_items.length) {
            md.push("");
            md.push("**Digest action items**");
            digest.action_items.forEach((i, idx) => md.push(`${idx + 1}. ${i}`));
        }
    }

    // Build action agenda from alerts + top-movers
    const actions = [];
    (data?.alerts || []).forEach((a) => {
        actions.push({
            priority: a.severity === "critical" ? "P0" : a.severity === "warning" ? "P1" : "P2",
            action: a.title || a.id,
            detail: a.body,
        });
    });
    (data?.top_movers || []).filter((m) => (m.change ?? 0) < -5).forEach((m) => {
        actions.push({
            priority: "P1",
            action: `Investigate rank drop on "${m.keyword}"`,
            detail: `Dropped ${Math.abs(m.change)} positions to #${m.position}. Volume ${fmt(m.volume)}, difficulty ${m.difficulty}. Check for content freshness, SERP feature shifts, or lost backlinks.`,
        });
    });
    md.push(actionAgenda(actions));

    return md.join("\n");
}

// ==================== KEYWORDS ====================
export function buildKeywordsMd({ project, rows }) {
    const md = [];
    md.push(header({
        title: "Keyword rank tracker",
        subtitle: "Every tracked keyword with its position, momentum, and recommended action",
        project,
        agentPrimer:
            "Each row is one tracked keyword. **Position** is today's SERP rank; **Change** is the delta vs. last check (negative = worse). The **Action** column tells the agent what to do about that keyword. Prioritize P0 (bleeding rank) first, then P1 (near top-10), then P2 (long-tail expansion).",
    }));

    if (!Array.isArray(rows) || !rows.length) {
        md.push("_No keywords tracked yet. Add keywords from the Keywords tab first._");
        return md.join("\n");
    }

    // Enrich each row with a recommended action
    const withAction = rows.map((k) => {
        const pos = k.position;
        const chg = k.change ?? 0;
        let action, priority;
        if (chg <= -5) { priority = "P0"; action = "Investigate rank drop — check content freshness, lost links, SERP-feature loss"; }
        else if (pos && pos <= 3) { priority = "P2"; action = "Defend top-3 — refresh content quarterly, monitor competitors"; }
        else if (pos && pos >= 4 && pos <= 10) { priority = "P1"; action = "Push to top-3 — expand H2/H3 coverage, add schema, earn 1–2 quality backlinks"; }
        else if (pos && pos >= 11 && pos <= 20) { priority = "P1"; action = "Break page-1 — content depth + internal linking + SERP-feature targeting"; }
        else if (pos && pos >= 21 && pos <= 50) { priority = "P2"; action = "Rewrite for intent — check search-intent alignment and topical cluster"; }
        else if (pos && pos > 50) { priority = "P3"; action = "Consider merging into a stronger cluster page or dropping if off-strategy"; }
        else { priority = "P2"; action = "No position yet — verify indexation with `site:` operator + Search Console"; }
        return { ...k, __action: action, __priority: priority };
    });

    md.push(`**${rows.length} keywords tracked**`);
    md.push("");
    md.push(table(
        ["Keyword", "Pos", "Prev", "Δ", "Volume", "Diff", "CPC", "AIO", "URL", "Action"],
        withAction.map((k) => [
            k.keyword,
            k.position ?? "—",
            k.previous_position ?? "—",
            (k.change ?? 0) > 0 ? `+${k.change}` : (k.change ?? "—"),
            fmt(k.volume),
            k.difficulty ?? "—",
            k.cpc ? `$${fmt(k.cpc, 2)}` : "—",
            k.aio || k.has_aio ? "✓" : "",
            k.url || "—",
            `**${k.__priority}** — ${k.__action}`,
        ]),
    ));

    // Action agenda: top 10 by priority
    const order = { P0: 0, P1: 1, P2: 2, P3: 3 };
    const agenda = withAction
        .slice()
        .sort((a, b) => (order[a.__priority] ?? 9) - (order[b.__priority] ?? 9))
        .slice(0, 15)
        .map((k) => ({
            priority: k.__priority,
            action: `"${k.keyword}" (currently #${k.position ?? "—"}, vol ${fmt(k.volume)})`,
            detail: k.__action,
        }));
    md.push(actionAgenda(agenda));

    return md.join("\n");
}

// ==================== SITE AUDIT ====================
export function buildAuditMd({ project, audit }) {
    const md = [];
    md.push(header({
        title: "Site audit",
        subtitle: "Every technical issue found, grouped by severity, with the concrete fix",
        project,
        agentPrimer:
            "Each issue lists **count** (how many pages are affected) and the exact **Fix** the agent must apply. Work top-down: `error/critical` blocks indexation and must be fixed first, `warning` degrades performance, `notice` is polish. **Core Web Vitals** section flags mobile-vs-desktop deltas — mobile score under 70 is a red flag.",
    }));

    if (!audit) return md.concat(["_No audit run yet. Trigger one from the Site Audit tab._"]).join("\n");

    md.push("## Overview");
    md.push(table(
        ["Metric", "Value"],
        [
            ["Health score", `${fmt(audit.health_score ?? audit.score, 1)} / 100`],
            ["Pages crawled", fmt(audit.pages_crawled)],
            ["Errors", fmt(audit.errors)],
            ["Warnings", fmt(audit.warnings)],
            ["Notices", fmt(audit.notices)],
            ["Run at", audit.run_at || audit.created_at || "—"],
        ],
    ));

    if (audit.core_web_vitals) {
        const cwv = audit.core_web_vitals;
        md.push("");
        md.push("## Core Web Vitals");
        md.push(table(
            ["Metric", "Value", "Threshold", "Status"],
            [
                ["LCP (Largest Contentful Paint)", `${cwv.lcp}s`, "≤ 2.5s", cwv.lcp <= 2.5 ? "PASS" : "FAIL"],
                ["CLS (Cumulative Layout Shift)", cwv.cls, "≤ 0.1", cwv.cls <= 0.1 ? "PASS" : "FAIL"],
                ["INP (Interaction to Next Paint)", `${cwv.inp}ms`, "≤ 200ms", cwv.inp <= 200 ? "PASS" : "FAIL"],
                ["Mobile PageSpeed", cwv.mobile_score, "≥ 70", cwv.mobile_score >= 70 ? "PASS" : "FAIL"],
                ["Desktop PageSpeed", cwv.desktop_score, "≥ 90", cwv.desktop_score >= 90 ? "PASS" : "FAIL"],
            ],
        ));
    }

    // Group issues by level
    const issues = audit.issues || [];
    const byLevel = { error: [], warning: [], notice: [] };
    issues.forEach((i) => (byLevel[i.level] || byLevel.notice).push(i));

    for (const level of ["error", "warning", "notice"]) {
        const bucket = byLevel[level];
        if (!bucket.length) continue;
        md.push("");
        md.push(`## ${level.toUpperCase()} · ${bucket.length} issue${bucket.length === 1 ? "" : "s"}`);
        bucket.forEach((issue, idx) => {
            md.push("");
            md.push(`### ${idx + 1}. ${issue.title}`);
            md.push(`- **Severity:** ${issue.severity || issue.level}`);
            md.push(`- **Pages affected:** ${issue.count}`);
            if (issue.fix) md.push(`- **Fix:** ${issue.fix}`);
            if (issue.example) md.push(`- **Example:** \`${issue.example}\``);
        });
    }

    // Build agenda from issues in priority order
    const agenda = issues
        .slice()
        .sort((a, b) => {
            const rank = { critical: 0, error: 1, warning: 2, notice: 3 };
            return (rank[a.severity ?? a.level] ?? 9) - (rank[b.severity ?? b.level] ?? 9);
        })
        .map((i) => ({
            priority: i.severity === "critical" || i.level === "error" ? "P0" : i.level === "warning" ? "P1" : "P2",
            action: `${i.title} (${i.count} page${i.count === 1 ? "" : "s"})`,
            detail: i.fix,
        }));
    md.push(actionAgenda(agenda));

    return md.join("\n");
}

// ==================== CONTENT / AEO ====================
export function buildContentMd({ project, content, target, result }) {
    const md = [];
    md.push(header({
        title: "Content / AEO analysis",
        subtitle: `Answer-engine + on-page audit for target keyword: \`${target || "—"}\``,
        project,
        agentPrimer:
            "The AEO score reflects readiness for Google's AI Overviews. Each **check** below is either PASS (✓) or FAIL (✗) with a weight. The agent should rewrite the analyzed content to turn every FAIL into a PASS, guided by the AI notes.",
    }));

    md.push(`- **Target keyword:** \`${target || "—"}\``);
    if (!result) return md.concat(["", "_Run an analysis to populate this report._"]).join("\n");
    md.push(`- **AEO Score:** ${result.score ?? "—"} / 100`);
    md.push(`- **Word count:** ${fmt((content || "").split(/\s+/).filter(Boolean).length)}`);

    if (Array.isArray(result.checks) && result.checks.length) {
        md.push("");
        md.push("## Checks");
        md.push(table(
            ["Check", "Pass", "Weight", "Fix"],
            result.checks.map((c) => [c.label, c.pass ? "✓" : "✗", c.weight ?? "", c.pass ? "—" : (c.fix || "Rewrite to satisfy this check")]),
        ));
    }

    if (result.ai_notes) {
        md.push("");
        md.push("## AI notes");
        md.push(result.ai_notes);
    }

    if (Array.isArray(result.suggestions) && result.suggestions.length) {
        md.push("");
        md.push("## Concrete rewrites suggested");
        result.suggestions.forEach((s, i) => md.push(`${i + 1}. ${s}`));
    }

    if (content) {
        md.push("");
        md.push("## Analyzed content");
        md.push("```markdown");
        md.push(content);
        md.push("```");
    }

    // Agenda: every failed check + suggestions
    const agenda = [];
    (result.checks || []).filter((c) => !c.pass).forEach((c) => {
        agenda.push({
            priority: (c.weight ?? 0) >= 10 ? "P0" : "P1",
            action: `Fix: ${c.label}`,
            detail: c.fix || "Rewrite the content section that fails this check.",
        });
    });
    md.push(actionAgenda(agenda));

    return md.join("\n");
}

// ==================== CANNIBALIZATION ====================
export function buildCannibalizationMd({ project, data }) {
    const md = [];
    md.push(header({
        title: "Keyword cannibalization",
        subtitle: "Every keyword where multiple URLs on your site compete against each other",
        project,
        agentPrimer:
            "Each conflict lists URLs competing for the same query with a similarity score. Recommended action is almost always: **pick a canonical URL, 301-redirect the weaker ones, and consolidate internal links to the survivor**. Similarity > 0.90 = critical; 0.75–0.90 = warning; < 0.75 = review manually.",
    }));

    const conflicts = data?.conflicts || data?.clusters || data?.groups || [];
    md.push(`- **Total keywords scanned:** ${fmt(data?.total_scanned)}`);
    md.push(`- **Conflicts detected:** ${conflicts.length}`);

    if (!conflicts.length) {
        return md.concat(["", "_No cannibalization detected. Rescan periodically as content grows._"]).join("\n");
    }

    md.push("");
    md.push("## Conflicts");
    conflicts.forEach((c, i) => {
        const sim = c.similarity ?? c.score ?? 0;
        const severity = sim >= 0.9 ? "CRITICAL" : sim >= 0.75 ? "WARNING" : "REVIEW";
        md.push("");
        md.push(`### ${i + 1}. \`${c.keyword || c.query}\` — ${severity}`);
        md.push(`- **Similarity:** ${fmt(sim, 2)}`);
        md.push(`- **Competing URLs (${(c.urls || []).length}):**`);
        (c.urls || []).forEach((u) => md.push(`  - \`${typeof u === "string" ? u : (u.url || u)}\`${u.position ? ` (pos ${u.position})` : ""}`));
        if (c.recommendation) md.push(`- **Recommendation:** ${c.recommendation}`);
    });

    // Agenda
    const agenda = conflicts.map((c) => {
        const sim = c.similarity ?? c.score ?? 0;
        const priority = sim >= 0.9 ? "P0" : sim >= 0.75 ? "P1" : "P2";
        return {
            priority,
            action: `Resolve conflict on "${c.keyword || c.query}"`,
            detail: `${(c.urls || []).length} URLs competing (similarity ${fmt(sim, 2)}). ${c.recommendation || "Pick the strongest URL as canonical, 301-redirect the others, and re-point internal links to the survivor."}`,
        };
    });
    md.push(actionAgenda(agenda));

    return md.join("\n");
}

// ==================== BACKLINKS ====================
export function buildBacklinksMd({ project, data }) {
    const md = [];
    md.push(header({
        title: "Backlink profile",
        subtitle: "Referring domains, quality distribution, and toxic-link disavow candidates",
        project,
        agentPrimer:
            "Each row is a referring domain. **DR** = Domain Rating (0–100, higher = more authority). **Type** = follow/nofollow. **Toxic score** > 60 → strong disavow candidate; > 40 → review. The Action agenda lists disavow candidates in `domain: <example.com>` format ready to paste into a Google `disavow.txt` file.",
    }));

    if (!data) return md.concat(["", "_No backlink data yet — click Refresh backlinks on the Backlinks tab._"]).join("\n");

    const totals = data.totals || {};
    md.push("## Totals");
    md.push(table(
        ["Metric", "Value"],
        Object.entries(totals).map(([k, v]) => [k, typeof v === "number" ? fmt(v) : v]),
    ));

    const sources = data.sources || data.backlinks || data.rows || [];
    if (sources.length) {
        // Split toxic vs. healthy
        const toxic = sources.filter((s) => (s.toxic_score ?? 0) >= 60);
        const review = sources.filter((s) => (s.toxic_score ?? 0) >= 40 && (s.toxic_score ?? 0) < 60);
        const healthy = sources.filter((s) => (s.toxic_score ?? 0) < 40);

        if (toxic.length) {
            md.push("");
            md.push(`## 🚨 Toxic — disavow candidates (${toxic.length})`);
            md.push(table(
                ["Domain", "DR", "Backlinks", "Type", "Toxic score", "First seen"],
                toxic.map((s) => [s.domain, s.dr ?? "—", fmt(s.backlinks), s.type || "—", s.toxic_score, s.first_seen || "—"]),
            ));
            md.push("");
            md.push("**Paste-ready disavow file lines:**");
            md.push("```");
            toxic.forEach((s) => md.push(`domain:${s.domain}`));
            md.push("```");
        }

        if (review.length) {
            md.push("");
            md.push(`## ⚠︎ Review manually (${review.length})`);
            md.push(table(
                ["Domain", "DR", "Backlinks", "Type", "Toxic score"],
                review.map((s) => [s.domain, s.dr ?? "—", fmt(s.backlinks), s.type || "—", s.toxic_score]),
            ));
        }

        if (healthy.length) {
            md.push("");
            md.push(`## ✓ Healthy referring domains (${healthy.length})`);
            md.push(table(
                ["Domain", "DR", "Backlinks", "Traffic", "Type"],
                healthy.slice(0, 100).map((s) => [s.domain, s.dr ?? "—", fmt(s.backlinks), fmt(s.traffic), s.type || "—"]),
            ));
        }
    }

    // Agenda
    const agenda = [];
    const toxic = (data.sources || []).filter((s) => (s.toxic_score ?? 0) >= 60);
    if (toxic.length) {
        agenda.push({
            priority: "P0",
            action: `Submit disavow file with ${toxic.length} toxic domains to Google Search Console`,
            detail: "Copy the disavow block above into a .txt file → Search Console → Disavow Links tool.",
        });
    }
    const review = (data.sources || []).filter((s) => (s.toxic_score ?? 0) >= 40 && (s.toxic_score ?? 0) < 60);
    if (review.length) {
        agenda.push({
            priority: "P1",
            action: `Manually review ${review.length} borderline referring domains`,
            detail: "Check anchor text patterns + linking page relevance. Add to disavow only if PBN/spam-signals confirmed.",
        });
    }
    md.push(actionAgenda(agenda));

    return md.join("\n");
}

// ==================== COMPETITORS ====================
export function buildCompetitorsMd({ project, data }) {
    const md = [];
    md.push(header({
        title: "Competitor landscape",
        subtitle: "Share of voice, keyword overlap, and gap analysis",
        project,
        agentPrimer:
            "The **Competitor table** shows overlap % vs. each competitor. The **Gap analysis** section lists keywords where competitors rank in the top 10 but you don't — these are your fastest wins. Sort agenda by opportunity_score (volume × difficulty_ratio).",
    }));

    const rows = data?.rows || data?.competitors || [];
    if (rows.length) {
        md.push("## Competitive landscape");
        md.push(table(
            ["Domain", "You?", "Visibility %", "Keywords", "Traffic", "Backlinks", "DR"],
            rows.map((c) => [c.domain, c.you ? "✓" : "", fmt(c.visibility, 1), fmt(c.keywords), fmt(c.traffic), fmt(c.backlinks), c.dr ?? "—"]),
        ));
    }

    const gaps = data?.gap || data?.gaps || [];
    if (gaps.length) {
        md.push("");
        md.push(`## Keyword gaps (${gaps.length})`);
        md.push(table(
            ["Keyword", "Volume", "Difficulty", "Your position", "Top competitor", "Their position"],
            gaps.map((g) => [g.keyword, fmt(g.volume), g.difficulty ?? "—", g.you_position ?? "—", g.top_competitor || "—", g.top_position ?? g.their_position ?? "—"]),
        ));
    }

    // Agenda: gap keywords sorted by opportunity
    const agenda = gaps
        .slice()
        .sort((a, b) => (b.volume ?? 0) - (a.volume ?? 0))
        .slice(0, 15)
        .map((g) => ({
            priority: (g.difficulty ?? 100) < 40 ? "P0" : (g.difficulty ?? 100) < 60 ? "P1" : "P2",
            action: `Target "${g.keyword}" (vol ${fmt(g.volume)}, diff ${g.difficulty})`,
            detail: `Competitor ${g.top_competitor || "?"} ranks #${g.top_position ?? g.their_position ?? "?"}. Write a longer, better-structured page than theirs and interlink from your topic hub.`,
        }));
    md.push(actionAgenda(agenda));

    return md.join("\n");
}

// ==================== REFERRALS ====================
export function buildReferralsMd({ data }) {
    const md = [];
    md.push(header({
        title: "Referral program",
        subtitle: "Signups, upgrades, and free months earned via your referral link",
        project: null,
        agentPrimer:
            "Tracks how the referral program is performing for this account. Every signup via the link earns 1 free month; every upgrade to Pro earns a bonus month. Use the event log to see which channel/push drove the most signups.",
    }));

    if (!data) return md.concat(["", "_No referral data yet._"]).join("\n");

    md.push("## Summary");
    md.push(table(
        ["Metric", "Value"],
        [
            ["Free months banked", fmt(data.credit_months)],
            ["Signups from your link", fmt(data.referred_signups)],
            ["Upgraded to Pro", fmt(data.referred_upgraded)],
            ["Referral code", data.referral_code || "—"],
        ],
    ));

    if (Array.isArray(data.events) && data.events.length) {
        md.push("");
        md.push(`## Event log (${data.events.length})`);
        md.push(table(
            ["Type", "Referred user", "Date"],
            [...data.events].reverse().map((e) => [
                e.type === "upgrade" ? "Upgrade → Pro" : "Signup",
                (e.referred_user_id || "—").slice(0, 8),
                e.at ? new Date(e.at).toISOString() : "—",
            ]),
        ));
    } else {
        md.push("");
        md.push("_No referral events yet — share your link to start earning free months._");
    }

    return md.join("\n");
}
// ==================== REPORTS ====================
export function buildReportsMd({ project, rep }) {
    const md = [];
    md.push(header({
        title: "Executive report",
        subtitle: "Wins, risks, and next actions in one shareable document",
        project,
        agentPrimer:
            "This is the client-facing summary. **Wins** are things that worked (celebrate + double down). **Risks** are things trending wrong (need attention). **Next actions** are the concrete todos for the next reporting period.",
    }));

    if (!rep) return md.concat(["", "_No report generated yet._"]).join("\n");
    if (rep.headline) md.push(`## ${rep.headline}`);
    if (rep.summary) { md.push(""); md.push(rep.summary); }
    if (Array.isArray(rep.wins) && rep.wins.length) {
        md.push("");
        md.push("## ✓ Wins");
        rep.wins.forEach((w) => md.push(`- ${w}`));
    }
    if (Array.isArray(rep.risks) && rep.risks.length) {
        md.push("");
        md.push("## ⚠︎ Risks");
        rep.risks.forEach((r) => md.push(`- ${r}`));
    }
    if (Array.isArray(rep.next_actions) && rep.next_actions.length) {
        md.push("");
        md.push("## → Next actions");
        rep.next_actions.forEach((a, i) => md.push(`${i + 1}. ${a}`));
    }

    const agenda = (rep.next_actions || []).map((a) => ({ priority: "P1", action: a }));
    md.push(actionAgenda(agenda));

    return md.join("\n");
}

// ==================== LOCAL SEO ====================
export function buildLocalSeoMd({ project, profileData, keywordsData }) {
    const md = [];
    md.push(header({
        title: "Local SEO toolkit",
        subtitle: "Google Business Profile + Map Pack rank tracking",
        project,
        agentPrimer:
            "The **Business profile** section is the real, live Google Business Profile for this location. The **Map pack rankings** table shows your real position vs. competitors for each tracked local keyword — rows with no position aren't yet ranking in the top results pulled.",
    }));

    const profile = profileData?.profile || {};
    const info = profileData?.business_info;
    md.push("## Business profile");
    md.push(`- **Business name:** ${profile.business_name || "—"}`);
    md.push(`- **Location:** ${profile.location_name || "—"}`);
    md.push(`- **Phone:** ${profile.phone || "—"}`);
    if (info) {
        md.push("");
        md.push(table(
            ["Field", "Value"],
            [
                ["Category", info.category || "—"],
                ["Address", info.address || "—"],
                ["Phone (GBP)", info.phone || "—"],
                ["Claimed", info.is_claimed ? "Yes" : "No"],
                ["Rating", info.rating ? `${info.rating} ★ (${fmt(info.reviews_count)} reviews)` : "—"],
                ["Website", info.website || "—"],
            ],
        ));
    } else {
        md.push("");
        md.push("_No live Google Business Profile data pulled yet — use \"Refresh profile\" on the Local SEO page._");
    }

    const rows = keywordsData?.keywords || [];
    md.push("");
    md.push(`## Map pack rankings (${rows.length})`);
    if (rows.length) {
        md.push(table(
            ["Keyword", "Location", "Position", "Matched listing", "Top competitor"],
            rows.map((r) => [
                r.keyword, r.location_name,
                r.metrics_available && r.position ? `#${r.position}` : "not ranking",
                r.matched_title || "—",
                r.top_competitors?.[0] ? `${r.top_competitors[0].title} (#${r.top_competitors[0].position})` : "—",
            ]),
        ));
    } else {
        md.push("_No local keywords tracked yet._");
    }

    const agenda = rows
        .filter((r) => r.metrics_available && (!r.position || r.position > 3))
        .slice(0, 15)
        .map((r) => ({
            priority: !r.position ? "P0" : "P1",
            action: `Improve map-pack ranking for "${r.keyword}" in ${r.location_name}`,
            detail: r.top_competitors?.[0]
                ? `${r.top_competitors[0].title} currently outranks you (#${r.top_competitors[0].position}). Add fresh photos, request reviews, and ensure NAP consistency and category match theirs.`
                : "Claim/verify your Google Business Profile and build local citations to enter the map pack.",
        }));
    md.push(actionAgenda(agenda));

    return md.join("\n");
}
